import React from 'react'
import Dialog from '../components/Dialog'
import MenuImportDialogContent from '../components/MenuImportDialogContent'
import MenuActivationDialogContent from '../components/MenuActivationDialogContent'
import updateMenuState, { UpdateMenuStateArgs } from '../mutations/updateMenuState'
import deleteImportedMenu from '../mutations/deleteImportedMenu'
import { useView } from './viewContext'
import { useCurrentUser } from './user'
import { handleMutationError } from './errors'

// Die Statuswechsel am Menü des gewählten Restaurants für die Menüverwaltung: Import eines
// Kartenstands samt Bericht, Aktivieren (als Preview oder normal), Ende der Preview, Veralten. Die
// Dokumente dazu druckt OnboardingDocumentsForm.
export const useMenuActions = () => {
	const { data: currentUser } = useCurrentUser()
	const { selectedRestaurantId } = useView()

	const selectedRestaurant = React.useMemo(
		() =>
			currentUser?.adminRestaurants?.find((restaurant) => restaurant.id === selectedRestaurantId) ??
			null,
		[currentUser?.adminRestaurants, selectedRestaurantId]
	)

	const [draftLoading, setDraftLoading] = React.useState(false)
	const [deleteImportLoading, setDeleteImportLoading] = React.useState(false)

	// Erst nur auswerten: der Root-User sieht im Dialog, was der Import eines Kartenstands anrichten
	// würde, und bestätigt ihn dort. Der Import wird eine neue Version im Status IMPORTED.
	// Mit `importable` false nur der Bericht, etwa für einen schon importierten Kartenstand.
	const handleImportSourceMenu = React.useCallback(
		(source: string, importable = true) => {
			if (!selectedRestaurantId) return
			Dialog.render({
				width: '1100px',
				renderContent: ({ dismissPortal }) => (
					<MenuImportDialogContent
						restaurantId={String(selectedRestaurantId)}
						source={source}
						importable={importable}
						dismissPortal={dismissPortal}
					/>
				),
			})
		},
		[selectedRestaurantId]
	)

	const handleConfirmDeleteImportedMenu = React.useCallback(async () => {
		if (!selectedRestaurantId) return
		setDeleteImportLoading(true)
		const result = await deleteImportedMenu({ restaurantId: String(selectedRestaurantId) })
		setDeleteImportLoading(false)
		if (!result.success) {
			handleMutationError('deleteImportedMenu', result.errors)
		}
	}, [selectedRestaurantId])

	const handleDeleteImportedMenu = React.useCallback(() => {
		Dialog.render({
			title: 'Importiertes Menü löschen',
			description:
				'Das importierte Menü wird vollständig entfernt, sodass es neu importiert werden kann. Fortfahren?',
			buttons: [
				{ id: 'cancel', label: 'Abbrechen' },
				{
					id: 'delete',
					label: 'Importiertes Menü löschen',
					onClick: handleConfirmDeleteImportedMenu,
					variant: 'outlined',
				},
			],
		})
	}, [handleConfirmDeleteImportedMenu])

	const handleUpdateMenuState = React.useCallback(
		async (
			menuVersion: number,
			newState: UpdateMenuStateArgs['newState'],
			preview?: boolean,
			importChecked?: boolean
		) => {
			if (!selectedRestaurantId) return
			setDraftLoading(true)
			const result = await updateMenuState({
				restaurantId: String(selectedRestaurantId),
				menuVersion,
				newState,
				preview,
				importChecked,
			})
			setDraftLoading(false)
			if (!result.success) {
				handleMutationError('updateMenuState', result.errors)
			}
		},
		[selectedRestaurantId]
	)

	const handleConfirmActivation = React.useCallback(
		(menuVersion: number, preview: boolean, importChecked: boolean) =>
			handleUpdateMenuState(menuVersion, 'ACTIVE', preview, importChecked),
		[handleUpdateMenuState]
	)

	// Bei einem Import muss im Dialog erst bestätigt werden, dass er gegen die Karte geprüft ist.
	const handleActivate = React.useCallback(
		(menuVersion: number, isImport: boolean) => {
			Dialog.render({
				renderContent: ({ dismissPortal }) => (
					<MenuActivationDialogContent
						title="Menü aktivieren"
						description={[
							'Die Version wird als aktives Menü veröffentlicht und ist danach in der App bestellbar.',
							'Aktiviert wird nur, wenn keine andere Version aktiv ist und alle Schritte des Onboardings abgehakt sind.',
						].join('\n\n')}
						confirmLabel="Aktivieren"
						menuVersion={menuVersion}
						preview={false}
						isImport={isImport}
						onConfirm={handleConfirmActivation}
						dismissPortal={dismissPortal}
					/>
				),
			})
		},
		[handleConfirmActivation]
	)

	const handleActivateAsPreview = React.useCallback(
		(menuVersion: number, isImport: boolean) => {
			Dialog.render({
				renderContent: ({ dismissPortal }) => (
					<MenuActivationDialogContent
						title="Als Preview aktivieren"
						description={[
							'Die Speisekarte geht ohne Vertrag online, mit dem Hinweis, dass das Restaurant sie noch nicht bestätigt hat.',
							'Kunden zahlen bar und ohne Service-Fee. Kommt eine Bestellung zustande, wird sie zurückgehalten, bis die Preview endet; das Restaurant bekommt weder Mail noch Anruf.',
						].join('\n\n')}
						confirmLabel="Als Preview aktivieren"
						menuVersion={menuVersion}
						preview
						isImport={isImport}
						onConfirm={handleConfirmActivation}
						dismissPortal={dismissPortal}
					/>
				),
			})
		},
		[handleConfirmActivation]
	)

	const handleGoLive = React.useCallback(
		(menuVersion: number) => {
			Dialog.render({
				title: 'Live schalten',
				description: [
					'Geht nur, wenn alle Schritte des Onboardings abgehakt sind.',
					'Das Menü gilt danach als vom Restaurant bestätigt. Neue Bestellungen laufen mit Service-Fee und der Zahlungsart nach Stripe-Konto.',
					'Zurückgehaltene Bestellungen gehen jetzt ans Restaurant: Es bekommt die Mail, wird angerufen und muss in der App annehmen.',
				].join('\n\n'),
				buttons: [
					{ id: 'cancel', label: 'Abbrechen' },
					{
						id: 'live',
						label: 'Live schalten',
						onClick: () => handleUpdateMenuState(menuVersion, 'ACTIVE', false),
						variant: 'outlined',
					},
				],
			})
		},
		[handleUpdateMenuState]
	)

	// Eine neue Version geht erst online, wenn die aktive veraltet ist.
	const handleDeactivateMenu = React.useCallback(
		(menuVersion: number) => {
			Dialog.render({
				title: 'Menü veralten',
				description:
					'Die Version wird auf „veraltet“ gesetzt. Ohne aktives Menü ist das Restaurant in der App nicht bestellbar, bis eine andere Version aktiviert ist. Fortfahren?',
				buttons: [
					{ id: 'cancel', label: 'Abbrechen' },
					{
						id: 'deactivate',
						label: 'Veralten',
						onClick: () => handleUpdateMenuState(menuVersion, 'OUTDATED'),
						variant: 'outlined',
					},
				],
			})
		},
		[handleUpdateMenuState]
	)

	return {
		selectedRestaurantId,
		selectedRestaurant,
		menuHistory: selectedRestaurant?.menuHistory,
		latestMenu: selectedRestaurant?.menuHistory?.[0],
		isRoot: Boolean(currentUser?.isRoot),
		draftLoading,
		deleteImportLoading,
		handleImportSourceMenu,
		handleDeleteImportedMenu,
		handleActivate,
		handleActivateAsPreview,
		handleGoLive,
		handleDeactivateMenu,
	}
}
